import { useCallback, useRef, useState } from 'react';

const DRAG_THRESHOLD_PX = 5;

export interface UseDragScrollResult<T extends HTMLElement> {
    isDragging: boolean;
    dragProps: {
        onPointerDown: (event: React.PointerEvent<T>) => void;
        onPointerMove: (event: React.PointerEvent<T>) => void;
        onPointerUp: (event: React.PointerEvent<T>) => void;
        onPointerCancel: (event: React.PointerEvent<T>) => void;
        onClickCapture: (event: React.MouseEvent<T>) => void;
        onDragStart: (event: React.DragEvent<T>) => void;
    };
}

export function useDragScroll<T extends HTMLElement = HTMLDivElement>(
    ref: React.RefObject<T | null>,
    enabled = true
): UseDragScrollResult<T> {
    const gesture = useRef({
        pending: false,
        dragging: false,
        startX: 0,
        startScroll: 0,
        moved: 0,
    });
    const [isDragging, setIsDragging] = useState(false);

    const onPointerDown = useCallback(
        (event: React.PointerEvent<T>) => {
            const element = ref.current;
            if (!enabled || !element) return;
            if (event.pointerType === 'touch' || event.button !== 0) return;

            gesture.current = {
                pending: true,
                dragging: false,
                startX: event.clientX,
                startScroll: element.scrollLeft,
                moved: 0,
            };
        },
        [enabled, ref]
    );

    const onPointerMove = useCallback(
        (event: React.PointerEvent<T>) => {
            const element = ref.current;
            if (!gesture.current.pending || !element) return;

            const delta = event.clientX - gesture.current.startX;
            const distance = Math.abs(delta);

            if (!gesture.current.dragging) {
                if (distance <= DRAG_THRESHOLD_PX) return;

                gesture.current.dragging = true;
                setIsDragging(true);
                event.currentTarget.setPointerCapture(event.pointerId);
            }

            gesture.current.moved = Math.max(gesture.current.moved, distance);
            element.scrollLeft = gesture.current.startScroll - delta;
        },
        [ref]
    );

    const endGesture = useCallback((event: React.PointerEvent<T>) => {
        if (!gesture.current.pending) return;

        const wasDragging = gesture.current.dragging;
        gesture.current.pending = false;
        gesture.current.dragging = false;

        if (!wasDragging) return;

        setIsDragging(false);
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    }, []);

    const onClickCapture = useCallback((event: React.MouseEvent<T>) => {
        if (gesture.current.moved <= DRAG_THRESHOLD_PX) return;

        event.preventDefault();
        event.stopPropagation();
        gesture.current.moved = 0;
    }, []);

    const onDragStart = useCallback((event: React.DragEvent<T>) => {
        if (gesture.current.dragging) event.preventDefault();
    }, []);

    return {
        isDragging,
        dragProps: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endGesture,
            onPointerCancel: endGesture,
            onClickCapture,
            onDragStart,
        },
    };
}
